// Mirrors the API's `ScheduleRules` (`service/automation/ScheduleRules.kt`) — same slot rule, same config
// reads, a slot fires at most once and never before the start date. Change both together.
// Instants are UTC (`startDate`, the sweep's clock, `lastScheduledExecutionAt`). The time of day, weekdays and day of
// month are read in the config's `zone` (DST-correct); a node without a usable one keeps the UTC calendar.

import {readTimeZone, wallTime, zonedInstant} from './timeZone';

/** The weekday codes the weekly editor stores, indexed by `Date#getUTCDay()` (0 = Sunday). */
const WEEKDAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const SCHEDULE_TYPES = ['once', 'daily', 'weekly', 'monthly'] as const;

type ScheduleType = typeof SCHEDULE_TYPES[number];

const DAY_MS = 24 * 60 * 60 * 1000;

export interface NextScheduledRun {
    /** The moment the run happens — `now` itself when the sweep is already due to fire for it. */
    at: Date;
    /** The slot has passed and the sweep (60 s tick) has not fired it yet. */
    due: boolean;
}

/**
 * The calendar the schedule counts days in. A day is the UTC midnight of its date, whatever the zone, so weekday,
 * day of month and day arithmetic stay plain UTC date math.
 */
interface Calendar {
    /** The day an instant falls on. */
    dayOf: (instant: number) => number;
    /** That day at the schedule's time of day. */
    slotOn: (day: number) => Date;
}

/** The API's `ScheduleRules.Schedule`. */
interface Schedule {
    type: ScheduleType;
    /** The first moment the schedule may fire; its time of day (in the calendar) is the schedule's time of day. */
    start: Date;
    /** Weekly only, as `getUTCDay()` numbers. Empty means "not configured" → the start date's weekday. */
    daysOfWeek: number[];
    /** Monthly only, 1..31. Null means "not configured" → the start date's day. */
    dayOfMonth: number | null;
    calendar: Calendar;
}

// `api.ts` turns response ISO strings into Dates, so a stored config may hold either; an unsaved edit is a string.
const toDate = (value: unknown): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value !== 'string' || !value.trim()) return null;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

// Absent means "once" (older nodes). Any other unknown value, "" included, never fires on the API.
const scheduleType = (raw: unknown): ScheduleType | null => {
    if (raw === null || raw === undefined) return 'once';
    if (typeof raw !== 'string') return null;

    const value = raw.trim().toLowerCase() as ScheduleType;
    return SCHEDULE_TYPES.includes(value) ? value : null;
};

/** `daysOfWeek` as "MON".."SUN"; unknown and non-string entries are dropped, as on the API. */
const daysOfWeek = (raw: unknown): number[] => {
    if (!Array.isArray(raw)) return [];

    const days = raw
        .map(code => (typeof code === 'string' ? WEEKDAY_CODES.indexOf(code.trim().toUpperCase()) : -1))
        .filter(index => index >= 0);
    return Array.from(new Set(days));
};

// A number from the editor, a string from older nodes, "" for "day of the start date".
// Unparsable or outside 1..31 is "not configured".
const dayOfMonth = (raw: unknown): number | null => {
    const value = typeof raw === 'number' ? raw : parseInt(String(raw ?? '').trim(), 10);
    return Number.isInteger(value) && value >= 1 && value <= 31 ? value : null;
};

const utcMidnight = (instant: number) => {
    const date = new Date(instant);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const calendarOf = (start: Date, zone: string | undefined): Calendar => {
    if (!zone) {
        const timeOfDay = start.getTime() - utcMidnight(start.getTime());
        return {dayOf: utcMidnight, slotOn: day => new Date(day + timeOfDay)};
    }

    const startWall = wallTime(start.getTime(), zone);
    const timeOfDay = startWall - utcMidnight(startWall);
    return {
        dayOf: instant => utcMidnight(wallTime(instant, zone)),
        slotOn: day => new Date(zonedInstant(day + timeOfDay, zone))
    };
};

// Null when it can never fire (no start date, unknown type).
const parseSchedule = (config: Record<string, any> | null | undefined): Schedule | null => {
    const start = toDate(config?.startDate);
    const type = scheduleType(config?.scheduleType);
    if (!start || !type) return null;

    return {
        type,
        start,
        daysOfWeek: daysOfWeek(config?.daysOfWeek),
        dayOfMonth: dayOfMonth(config?.dayOfMonth),
        calendar: calendarOf(start, readTimeZone(config?.zone))
    };
};

/** False when the API's sweep can never fire this trigger: no usable start date (the Starts ×) or an unknown type. */
export const scheduleCanFire = (config: Record<string, any> | null | undefined) => parseSchedule(config) !== null;

const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const startDay = (schedule: Schedule) => new Date(schedule.calendar.dayOf(schedule.start.getTime()));

// Clamped to the month's length (day 31 fires on April 30th), so no month is skipped.
const monthlySlot = (schedule: Schedule, year: number, month: number) => {
    const day = schedule.dayOfMonth ?? startDay(schedule).getUTCDate();
    return schedule.calendar.slotOn(Date.UTC(year, month, Math.min(day, daysInMonth(year, month))));
};

const weekdays = (schedule: Schedule) => (
    schedule.daysOfWeek.length ? schedule.daysOfWeek : [startDay(schedule).getUTCDay()]
);

const weekdayOf = (day: number) => new Date(day).getUTCDay();

// The API's `dueTriggerPoint`. No catch-up beyond the current day (week, month), as on the API.
const dueSlot = (schedule: Schedule, last: Date | null, now: Date): Date | null => {
    const {calendar} = schedule;
    const today = calendar.dayOf(now.getTime());

    const candidate = (() => {
        switch (schedule.type) {
            case 'once':
                return schedule.start;
            case 'daily':
                return calendar.slotOn(today);
            case 'weekly': {
                // The sweep picks the latest slot not in the future within the last seven days.
                const days = weekdays(schedule);
                for (let back = 0; back <= 6; back++) {
                    const day = today - back * DAY_MS;
                    if (!days.includes(weekdayOf(day))) continue;

                    const slot = calendar.slotOn(day);
                    if (slot.getTime() <= now.getTime()) return slot;
                }
                return null;
            }
            case 'monthly': {
                const date = new Date(today);
                return monthlySlot(schedule, date.getUTCFullYear(), date.getUTCMonth());
            }
        }
    })();

    if (!candidate) return null;
    if (candidate.getTime() > now.getTime()) return null;
    if (candidate.getTime() < schedule.start.getTime()) return null;
    if (last && last.getTime() >= candidate.getTime()) return null;
    return candidate;
};

// The earliest slot strictly after `floor`.
const nextSlot = (schedule: Schedule, floor: Date): Date | null => {
    const {calendar} = schedule;
    const ahead = (slot: Date) => slot.getTime() > floor.getTime();
    const today = calendar.dayOf(floor.getTime());

    switch (schedule.type) {
        case 'once':
            return ahead(schedule.start) ? schedule.start : null;
        case 'daily': {
            const slot = calendar.slotOn(today);
            return ahead(slot) ? slot : calendar.slotOn(today + DAY_MS);
        }
        case 'weekly': {
            const candidates = weekdays(schedule).map(weekday => {
                const day = today + ((weekday - weekdayOf(today) + 7) % 7) * DAY_MS;
                const slot = calendar.slotOn(day);
                return ahead(slot) ? slot : calendar.slotOn(day + 7 * DAY_MS);
            });
            return candidates.reduce((a, b) => (a.getTime() <= b.getTime() ? a : b));
        }
        case 'monthly': {
            const date = new Date(today);
            const year = date.getUTCFullYear();
            const month = date.getUTCMonth();
            const thisMonth = monthlySlot(schedule, year, month);
            if (ahead(thisMonth)) return thisMonth;

            return monthlySlot(schedule, month === 11 ? year + 1 : year, (month + 1) % 12);
        }
    }
};

/**
 * Null when the trigger has no next run: no usable start date, an unknown schedule type, or a "once" that fired.
 * `lastScheduledExecutionAt` is the slot the flow last fired for, straight off the flow.
 */
export function nextScheduledRun(
    config: Record<string, any>,
    lastScheduledExecutionAt: Date | string | null | undefined,
    now: Date
): NextScheduledRun | null {
    const schedule = parseSchedule(config);
    if (!schedule) return null;

    // The API fires a "once" only if it never fired, even when its start date moved forward since.
    const last = toDate(lastScheduledExecutionAt);
    if (schedule.type === 'once' && last) return null;

    if (dueSlot(schedule, last, now)) {
        return {at: now, due: true};
    }

    // No slots exist before the start date, so a schedule that has not begun searches from just before it.
    const floor = now.getTime() >= schedule.start.getTime() ? now : new Date(schedule.start.getTime() - 1);
    const at = nextSlot(schedule, floor);
    return at ? {at, due: false} : null;
}
